import type { FileTransferConfig } from "./types";

export interface FileHeaderMetadata {
  contentType: string | null;
  contentLength: number | null;
  filename: string | null;
  fileType: string | null;
}

const parseContentDisposition = (header: string | null): string | null => {
  if (!header) return null;
  const filenameMatch = header.match(/filename="([^"]+)"/);
  return filenameMatch?.[1] ?? null;
};

const parseNineamhealthFileAttributes = (
  header: string | null
): { filename: string | null; fileType: string | null } => {
  if (!header) return { filename: null, fileType: null };
  try {
    const parsed = JSON.parse(header) as {
      "file.name"?: string;
      "file.type"?: string;
    };
    return {
      filename: parsed["file.name"] ?? null,
      fileType: parsed["file.type"] ?? null
    };
  } catch {
    return { filename: null, fileType: null };
  }
};

/**
 * Parses file metadata from HTTP response headers.
 * Extracts content-type, content-length, filename, and file type.
 *
 * Filename is extracted from Content-Disposition header first,
 * falling back to x-nineamhealth-file-attributes header.
 */
export const parseFileHeaders = (headers: Headers): FileHeaderMetadata => {
  const contentType = headers.get("Content-Type");
  const contentLengthHeader = headers.get("Content-Length");
  const contentLength = contentLengthHeader
    ? parseInt(contentLengthHeader, 10)
    : null;

  const contentDisposition = headers.get("Content-Disposition");
  const nineamhealthAttributes = headers.get("x-nineamhealth-file-attributes");

  const filenameFromDisposition = parseContentDisposition(contentDisposition);
  const { filename: filenameFromAttributes, fileType } =
    parseNineamhealthFileAttributes(nineamhealthAttributes);

  return {
    contentType,
    contentLength: Number.isNaN(contentLength) ? null : contentLength,
    filename: filenameFromDisposition ?? filenameFromAttributes,
    fileType
  };
};

/**
 * Fetches only the file headers without downloading the body.
 * Uses a Range request for 0 bytes to minimize data transfer.
 *
 * @param fileId - The file ID to fetch headers for
 * @param config - Configuration with API URL and auth token
 * @returns Parsed file header metadata or null on error
 */
export const fetchFileHeaders = async (
  fileId: string,
  config: FileTransferConfig
): Promise<FileHeaderMetadata | null> => {
  try {
    const response = await fetch(
      `${config.apiBaseUrl}/v1/files/${fileId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${config.getAccessToken()}`,
          Range: "bytes=0-0"
        }
      }
    );

    if (!response.ok && response.status !== 206) {
      // Consume body to release connection
      await response.arrayBuffer();
      return null;
    }

    const metadata = parseFileHeaders(response.headers);
    // Consume body to release connection (prevents requests stuck in pending)
    await response.arrayBuffer();
    return metadata;
  } catch {
    return null;
  }
};
